/**
 * `POST {o}/pricing/preview` (05-api.md "Orders"): price a cart without saving it.
 * The cart names products, variants, options and add-ons only; prices, tax groups
 * and tax-inclusive flags come from the outlet's published menu, and tax rules,
 * charges, discounts and round-off from its stored setup (CLAUDE.md rule 1). Also
 * `PricingResult`, the calculator's output on the wire (money as integer paise).
 *
 * The result schema mirrors `PricingResult` in `packages/pricing/src/types.ts`
 * field for field (apps/api tests check the two stay assignable both ways).
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { uuidSchema } from './ids.js';
import {
  basisPointsSchema,
  nonNegativePaiseSchema,
  paiseSchema,
  percentBasisPointsSchema,
} from './money.js';
import { PRICE_CONTEXTS } from './org.js';
import { TAX_RULE_COMPONENTS } from './pricing.js';

/** `PricingContext.orderType`. */
export const PRICING_ORDER_TYPES = ['dine_in', 'takeaway', 'delivery', 'aggregator'] as const;
export const SUPPLY_TYPES = ['intra_state', 'inter_state'] as const;

/** Client-side ids of lines and applied discounts (an order line or order discount id, or any tag). */
const refSchema = z.string().trim().min(1).max(64);
const qtyMilliSchema = z.int().min(1).max(10_000_000);

export const MAX_PREVIEW_LINES = 500;

/** An add-on chosen with a line, `qtyMilli` per one unit of the line (default 1000). */
export const pricingPreviewAddonSchema = z.object({
  addonId: uuidSchema,
  qtyMilli: qtyMilliSchema.optional(),
});

/**
 * A cart line: what the diner or cashier chose, never a price (CLAUDE.md rule 1).
 * The server resolves the unit price, tax group, tax-inclusive flag and
 * discountability from the outlet's published menu for the order's context,
 * section and time (menu, 03-modules.md §3 price model).
 */
export const pricingPreviewLineSchema = z.object({
  id: refSchema,
  productId: uuidSchema,
  /** Required when the product has variants (Half/Full). */
  variantId: uuidSchema.nullable().optional(),
  qtyMilli: qtyMilliSchema,
  /** Options of the product's option groups (each group's min/max apply). */
  optionIds: z
    .array(uuidSchema)
    .max(50)
    .refine((v) => new Set(v).size === v.length, { message: 'Each option at most once' })
    .optional(),
  /** Add-ons of the product's add-on groups (each group's min/max apply). */
  addons: z
    .array(pricingPreviewAddonSchema)
    .max(50)
    .refine((v) => new Set(v.map((a) => a.addonId)).size === v.length, {
      message: 'Each add-on at most once',
    })
    .optional(),
  complimentary: z.boolean().optional(),
});
export type PricingPreviewLine = z.output<typeof pricingPreviewLineSchema>;

/**
 * A discount on the cart: one of the outlet's discounts (`discountId`; its
 * calc, value and categories come from the server; an `item` discount names the
 * lines in `lineIds`), or a manual one the cashier types in.
 */
export const pricingPreviewDiscountSchema = z.union([
  z.object({
    id: refSchema,
    discountId: uuidSchema,
    lineIds: z.array(refSchema).max(MAX_PREVIEW_LINES).optional(),
  }),
  z.object({
    id: refSchema,
    manual: z.literal(true),
    name: z.string().max(60).optional(),
    calc: z.literal('percent'),
    rateBp: percentBasisPointsSchema,
    maxPaise: nonNegativePaiseSchema.nullable().optional(),
    lineIds: z.array(refSchema).max(MAX_PREVIEW_LINES).optional(),
  }),
  z.object({
    id: refSchema,
    manual: z.literal(true),
    name: z.string().max(60).optional(),
    calc: z.literal('flat'),
    amountPaise: nonNegativePaiseSchema,
    lineIds: z.array(refSchema).max(MAX_PREVIEW_LINES).optional(),
  }),
]);
export type PricingPreviewDiscount = z.output<typeof pricingPreviewDiscountSchema>;

export const pricingPreviewRequestSchema = z.object({
  context: z.object({
    orderType: z.enum(PRICING_ORDER_TYPES),
    /** The pricing channel (`order.channel`). */
    channel: z.enum(PRICE_CONTEXTS),
    /** The `order.source` tag (pos, captain, qr, …); default `pos`. */
    source: z.string().trim().min(1).max(30).optional(),
    /** A section of this outlet (section prices; charges may apply per section). */
    sectionId: uuidSchema.nullable().optional(),
    /**
     * The time the order is for (timing-group prices and dayparts, and the default
     * tax date); default now. A takeaway slot later today, for example.
     */
    at: isoTimestampSchema.optional(),
    /** Whose tax rates apply; default today in the outlet's timezone (11-open-questions.md Q15). */
    taxDate: businessDateSchema.optional(),
    /** Default `intra_state`. */
    supply: z.enum(SUPPLY_TYPES).optional(),
    complimentary: z.boolean().optional(),
  }),
  lines: z.array(pricingPreviewLineSchema).min(1).max(MAX_PREVIEW_LINES),
  discounts: z.array(pricingPreviewDiscountSchema).max(20).optional(),
});
export type PricingPreviewRequest = z.output<typeof pricingPreviewRequestSchema>;

// ---------------------------------------------------------------------------
// PricingResult on the wire

const taxComponent = z.enum(TAX_RULE_COMPONENTS);

const taxAmounts = {
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  cessPaise: paiseSchema,
  taxPaise: paiseSchema,
};

const appliedRateSchema = z.object({ component: taxComponent, rateBp: basisPointsSchema });

const componentResultSchema = z.object({
  ...taxAmounts,
  part: z.string(),
  taxGroupId: z.string().nullable(),
  taxInclusive: z.boolean(),
  rates: z.array(appliedRateSchema),
  grossPaise: paiseSchema,
  discountPaise: paiseSchema,
  netPaise: paiseSchema,
  preTaxGrossPaise: paiseSchema,
  preTaxDiscountPaise: paiseSchema,
  taxablePaise: paiseSchema,
  totalPaise: paiseSchema,
});

const lineResultSchema = z.object({
  ...taxAmounts,
  lineId: z.string(),
  complimentary: z.boolean(),
  complimentaryPaise: paiseSchema,
  grossPaise: paiseSchema,
  discountPaise: paiseSchema,
  netPaise: paiseSchema,
  preTaxGrossPaise: paiseSchema,
  preTaxDiscountPaise: paiseSchema,
  taxablePaise: paiseSchema,
  totalPaise: paiseSchema,
  discounts: z.array(z.object({ id: z.string(), amountPaise: paiseSchema })),
  components: z.array(componentResultSchema),
});

const discountResultSchema = z.object({
  id: z.string(),
  discountId: z.string().nullable(),
  calc: z.enum(['percent', 'flat']),
  basePaise: paiseSchema,
  requestedPaise: paiseSchema,
  amountPaise: paiseSchema,
  capped: z.boolean(),
  shares: z.array(z.object({ lineId: z.string(), amountPaise: paiseSchema })),
});

const chargeResultSchema = z.object({
  ...taxAmounts,
  id: z.string(),
  kind: z.enum(['packaging', 'service', 'delivery', 'other']),
  calc: z.enum(['percent', 'flat']),
  taxable: z.boolean(),
  taxGroupId: z.string().nullable(),
  rates: z.array(appliedRateSchema),
  basePaise: paiseSchema,
  amountPaise: paiseSchema,
  totalPaise: paiseSchema,
});

const taxSummaryRowSchema = z.object({
  component: taxComponent,
  rateBp: basisPointsSchema,
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
});

const totalsSchema = z.object({
  ...taxAmounts,
  subtotalPaise: paiseSchema,
  preTaxDiscountPaise: paiseSchema,
  discountAppliedPaise: paiseSchema,
  chargesPaise: paiseSchema,
  complimentaryPaise: paiseSchema,
  taxablePaise: paiseSchema,
  preRoundPaise: paiseSchema,
  roundOffPaise: paiseSchema,
  totalPaise: paiseSchema,
});

/** `PricingResult` (packages/pricing) with money as integer paise. */
export const pricingResultSchema = z.object({
  version: z.string(),
  lines: z.array(lineResultSchema),
  discounts: z.array(discountResultSchema),
  charges: z.array(chargeResultSchema),
  taxes: z.array(taxSummaryRowSchema),
  totals: totalsSchema,
  warnings: z.array(z.string()),
  trace: z.array(z.string()),
});

/** Whether the caller may apply a discount without a manager (their role's discount limit). */
export const discountCheckSchema = z.object({
  /** The discount's `id` in the request. */
  id: z.string(),
  withinLimit: z.boolean(),
  /** What the till asks a manager for when the discount is above the limit. */
  requiredPermission: z.enum(['orders.discount', 'orders.discount.above_limit']).nullable(),
  /** The discount asks for a reason when applied. */
  reasonRequired: z.boolean(),
});

/** A cart line as the server resolved it from the published menu. */
export const resolvedLineSchema = z.object({
  id: z.string(),
  productId: uuidSchema,
  variantId: uuidSchema.nullable(),
  categoryId: uuidSchema,
  name: z.string(),
  unitPricePaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean(),
  taxGroupId: uuidSchema.nullable(),
  discountable: z.boolean(),
  modifiers: z.array(
    z.object({
      kind: z.enum(['option', 'addon']),
      refId: uuidSchema,
      name: z.string(),
      unitPricePaise: nonNegativePaiseSchema,
      qtyMilli: z.int(),
      taxInclusive: z.boolean(),
    }),
  ),
});
export type ResolvedLine = z.output<typeof resolvedLineSchema>;

export const pricingPreviewResponseSchema = z.object({
  /** The tax date used. */
  taxDate: businessDateSchema,
  /** The published menu version the lines were priced from. */
  menuVersion: z.int(),
  lines: z.array(resolvedLineSchema),
  result: pricingResultSchema,
  discountChecks: z.array(discountCheckSchema),
});
